import axios from "axios";
import HelperService from "../services/helper-service";
import { OFFLINE_REDIRECT_CANCEL } from "../shared/utilty/constants";
import { KuikaAppManager } from "../shared/utilty/kuika-app-manager";
import LocalStorageService from "./local-storage-service";

declare const window: any;

const backendUrl = KuikaAppManager.GetBackendUrl();
let isTokenRefreshing = false;
let failedQueue = [] as any;
const localStorageService = LocalStorageService.getService();

const MAX_RETRIES: number = 3;
const RETRY_DELAY: number = 10000;



axios.interceptors.response.use(undefined, async (error) => {
  // Skip if request was cancelled (e.g., offline redirect)
  if (axios.isCancel(error) || !error.config) {
    return Promise.reject(error);
  }

  // Skip retry for rate limiting responses
  if (error.response && error.response.status === 429) {
    return Promise.reject(error);
  }

  const config = error.config;
  config.__retryCount = config.__retryCount || 0;
  const shouldRetry = !error.response || error.code === "ECONNREFUSED" || error.code === "ERR_NETWORK";

  // Skip retries when offline - fail fast to allow cache fallback
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    
    return Promise.reject(error);
  }

  if (shouldRetry && (MAX_RETRIES === -1 || config.__retryCount < MAX_RETRIES)) {
    config.__retryCount += 1;

    console.log(`Retrying request (${config.__retryCount}/${MAX_RETRIES}): ${config.url}`, {
      method: config.method,
      errorCode: error.code,
      errorMessage: error.message
    });

    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));

    return axios(config);
  }

  return Promise.reject(error);
});

const processQueue = (error, token = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

// Add a request interceptor
axios.interceptors.request.use(
  (config) => {
    

    const urlItems = document.location.pathname.substring(1).split(/\//);
    const token = localStorageService.getAccessToken();
    const language = localStorage.getItem("ml");
    if (language && language.length) {
      config.headers.cultureName = language;
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;

      // TODO: Buna direkt gerek olmayabilir. Simdilik silmiyorum.
      if (urlItems[0] && urlItems[0] !== "") {
        config.headers.domainName = urlItems[0];
      }
    }
    if (window.kuika && window.kuika?.project && window.kuika?.project.domainId) {
      config.headers.domainId = window.kuika?.project.domainId;
    }
    const nextAudit = KuikaAppManager.consumeNextRequestAudit();
    if (nextAudit?.key) {
      config.headers["x-audit-key"] = nextAudit.key;
      config.headers["x-audit-name"] = nextAudit.name;
      if (nextAudit.source) {
        config.headers["x-audit-source"] = nextAudit.source;
      }
      if (nextAudit.data !== undefined) {
        config.headers["x-audit-data"] = JSON.stringify(nextAudit.data);
      }
    }
    return config;
  },
  (error) => {
    Promise.reject(error);
  }
);

// Add a response interceptor
axios.interceptors.response.use(
  async (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (!error.response) {
      return Promise.reject(error);
    }

    if (error.response.status === 401 && originalRequest.url === `${backendUrl}/auth/login`) {
      return Promise.reject(error);
    }

    if (error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      if (isTokenRefreshing === false) {
        isTokenRefreshing = true;
        const refreshToken = localStorageService.getRefreshToken();

        if (!refreshToken || refreshToken === "null" || refreshToken === "") {
          localStorageService.clearToken();

          if (!HelperService.getIsInLoginPage()) {
            const redirectUri = window.location.pathname + window.location.search;
            HelperService.gotoLogin(redirectUri);
          }

          return;
        }

        return axios
          .post(`${backendUrl}/auth/refreshtoken`, {
            refreshtoken: refreshToken
          })
          .then((res) => {
            isTokenRefreshing = false;
            if (res && res.status === 200) {
              localStorageService.setToken(res.data);
              axios.defaults.headers.common.Authorization = `Bearer ${localStorageService.getAccessToken()}`;
              processQueue(null, res.data.token);
              return axios(originalRequest);
            }
          })
          .catch((e) => {
            localStorageService.clearToken();
            HelperService.gotoLogin();
          });
      }
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;

          if (originalRequest.url === `${backendUrl}/auth/getistokenvalid`) {
            const refreshToken = localStorageService.getRefreshToken();
            if (refreshToken && originalRequest.data) {
              originalRequest.data = {
                token: localStorage.getItem("refreshToken")
              };
            }
          }

          return axios(originalRequest);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    }

    if (error.response.status >= 400 && error.config.url.indexOf("refreshtoken") > -1) {
      localStorageService.clearToken();
      HelperService.gotoLogin();
    }
    return Promise.reject(error);
  }
);
